import React, { useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Cpu,
  Wrench,
  UserCog,
  Car,
  Search,
  LogOut,
  Settings,
  Truck,
  ChevronRight,
  ShieldCheck,
  KeyRound,
  Menu,
  X,
  Smartphone,
  HardDrive,
  MailCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SettingsPanel from "@/components/SettingsPanel";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import InstallPrompt from "@/components/InstallPrompt";
import InstallAppButton from "@/components/InstallAppButton";
import usePWABackGuard from "@/hooks/usePWABackGuard";

const MAIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, tid: "nav-dashboard" },
  { to: "/vehicles", label: "Customer Vehicle Installation Details", icon: Car, tid: "nav-vehicles" },
  { to: "/search", label: "Search Details", icon: Search, tid: "nav-search" },
];

const MASTERS_NAV = [
  { to: "/masters/customers", label: "Customer Details", icon: Users, tid: "nav-customers" },
  { to: "/masters/providers", label: "SIM Service Provider", icon: Building2, tid: "nav-providers" },
  { to: "/masters/sims", label: "SIMs Details", icon: Smartphone, tid: "nav-sims" },
  { to: "/masters/device-models", label: "Device Model", icon: Cpu, tid: "nav-device-models" },
  { to: "/masters/device-details", label: "Device Details", icon: HardDrive, tid: "nav-device-details" },
  { to: "/masters/installation-types", label: "Installation Type", icon: Wrench, tid: "nav-installation-types" },
  { to: "/masters/employees", label: "Installation Employees", icon: UserCog, tid: "nav-employees" },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Users", icon: ShieldCheck, tid: "nav-users" },
  { to: "/admin/renewals", label: "Renewal Reminders", icon: MailCheck, tid: "nav-renewals" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [pwOpen, setPwOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Prevent the installed PWA from closing on the first back press (mobile UX)
  usePWABackGuard();

  // Close mobile drawer on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const renderSidebar = (variant = "desktop") => {
    const sx = variant === "mobile" ? "-mobile" : "";
    return (
    <>
      <div
        className="p-4 border-b vsm-border flex items-center gap-3"
        style={{ backgroundColor: "white" }}
      >
        <img
          src="/ramki-logo.jpg"
          alt="Ramki Technologies"
          data-testid="company-logo"
          className="h-12 w-auto object-contain"
        />
        <div className="leading-tight flex-1 min-w-0">
          <div className="vsm-overline" style={{ color: "#71717a" }}>Vehicle SIM Mapping</div>
          <div className="font-bold text-xs tracking-tight truncate" style={{ color: "#1d4ed8" }}>
            RAMKI TECHNOLOGIES
          </div>
        </div>
        {/* Mobile close button inside drawer */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-close"
          className="lg:hidden p-1.5 hover:bg-zinc-100"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-5 pb-2">
          <div className="vsm-overline">Workspace</div>
        </div>
        {MAIN_NAV.map((item) => <NavItem key={item.to} {...item} onNavigate={() => setMobileOpen(false)} />)}

        <div className="px-5 pt-6 pb-2">
          <div className="vsm-overline">Masters</div>
        </div>
        {MASTERS_NAV.map((item) => <NavItem key={item.to} {...item} onNavigate={() => setMobileOpen(false)} />)}

        {user?.role === "admin" && (
          <>
            <div className="px-5 pt-6 pb-2">
              <div className="vsm-overline">Administration</div>
            </div>
            {ADMIN_NAV.map((item) => <NavItem key={item.to} {...item} onNavigate={() => setMobileOpen(false)} />)}
          </>
        )}

        <div className="px-5 pt-6 pb-2">
          <div className="vsm-overline">Account</div>
        </div>
        <button
          type="button"
          onClick={() => setPwOpen(true)}
          data-testid="nav-change-password"
          className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 border-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100"
        >
          <KeyRound className="w-4 h-4 shrink-0" />
          <span className="truncate text-left">Change Password</span>
        </button>
        <div className="px-2">
          <InstallAppButton />
        </div>
      </nav>

      <div className="border-t vsm-border p-4 space-y-1">
        <button
          onClick={() => setSettingsOpen(true)}
          data-testid="settings-trigger"
          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm font-medium transition-colors"
        >
          <Settings className="w-4 h-4" />
          <span>Settings</span>
          <ChevronRight className="w-4 h-4 ml-auto" />
        </button>

        <div className="px-3 py-2.5 border vsm-border flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/profile")}
            data-testid={`sidebar-profile-trigger${sx}`}
            title="Edit your profile"
            className="w-8 h-8 vsm-primary-bg flex items-center justify-center font-bold text-xs overflow-hidden shrink-0 hover:opacity-90"
          >
            {user?.photo ? (
              <img src={user.photo} alt={user?.username || "profile"} className="w-full h-full object-cover" />
            ) : (
              <span>{(user?.username || "?").slice(0, 2).toUpperCase()}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="leading-tight overflow-hidden text-left flex-1 min-w-0"
            data-testid={`sidebar-profile-name${sx}`}
          >
            <div className="text-sm font-semibold truncate" data-testid={`current-username${sx}`}>
              {user?.name || user?.username}
            </div>
            <div className="text-xs vsm-muted truncate uppercase tracking-wide">{user?.role}</div>
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title="Logout"
            className="ml-auto p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
    );
  };

  return (
    <div className="vsm-shell flex">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 vsm-sidebar-surface border-b vsm-border flex items-center gap-2 p-3"
           data-testid="mobile-topbar">
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-trigger"
          className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <img src="/ramki-logo.jpg" alt="Ramki Technologies" className="h-8 w-auto object-contain" />
        <div className="leading-tight">
          <div className="font-bold text-xs tracking-tight" style={{ color: "#1d4ed8" }}>
            RAMKI TECHNOLOGIES
          </div>
          <div className="text-[10px] vsm-muted uppercase tracking-wider">Vehicle SIM Mapping</div>
        </div>
        <button
          onClick={() => setSettingsOpen(true)}
          data-testid="mobile-settings"
          className="ml-auto p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          aria-label="Settings"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden lg:flex w-72 shrink-0 border-r vsm-border vsm-sidebar-surface min-h-screen flex-col sticky top-0 h-screen"
      >
        {renderSidebar("desktop")}
      </aside>
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-backdrop"
        />
        <aside
          data-testid="mobile-drawer"
          className={`absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] vsm-sidebar-surface border-r vsm-border flex flex-col transform transition-transform ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {renderSidebar("mobile")}
        </aside>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </main>

      <SettingsPanel open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <InstallPrompt />
    </div>
  );
}

function NavItem({ to, label, icon: Icon, exact, tid, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={tid}
      onClick={() => onNavigate && onNavigate()}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-colors ${
          isActive
            ? "vsm-primary-text border-current bg-zinc-100 dark:bg-zinc-800"
            : "border-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}
